import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  notification,
  Row,
  Spin,
  Collapse,
  Empty,
} from "antd";
import CartItem from "../../components/Restaurant/CartItem";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  changeQuantity,
  setRestaurant,
} from "../../slices/restaurantCartSlice";
import {
  _single_business_data,
  _validate_basket_stock,
  _user_dashboard_shop,
} from "../../common/axios_services";
import { LoadingOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import LeaveMessageDrawer from "../../components/LeaveMessageDrawer";
import FooterDisplay from "../../components/footer";
import BackToRestaurant from "./BackToRestaurant";
import useMinimumOrder from "../../hooks/useMinimumOrder";
import { roundDisplayPrice } from "../../utils/priceHelpers";
import GroupAccountBanner from "../../components/GroupAccountBanner";
import { normalizeBusinessType } from "../../utils/businessTypeHelpers";
import CartDiscountChoiceModal from "../../components/CartDiscountChoiceModal";

const SingleCartCard = ({
  title,
  subtitle,
  items,
  businessType,
  handleDelete,
  handleGoBack,
  onCheckout,
  rateConfig,
  restaurantData,
  minimumOrder,
  deliveryType,
}) => {
  const totalItems = items.reduce(
    (total, item) => total + (item.quantity || 0),
    0,
  );

  const totalAmount = items.reduce((total, item) => {
    const rawPrice =
      item?.detail?.public_price || item?.detail?.priceWithMarkup || 0;
    const price = roundDisplayPrice(rawPrice);
    let itemTotal = price * (item.quantity || 0);
    const extrasTotal = (item.extras || []).reduce((extraTotal, extra) => {
      if (
        extra?.id === "container_cost" ||
        extra?.item?.name === "Container Cost" ||
        extra?.name === "Container Cost"
      ) {
        return extraTotal;
      }
      const extraPrice = roundDisplayPrice(parseFloat(extra?.price) || 0);
      const extraQuantity = extra?.quantity || 0;
      return extraTotal + extraPrice * extraQuantity;
    }, 0);
    itemTotal += extrasTotal;
    return total + itemTotal;
  }, 0);

  if (items.length === 0) return null;

  return (
    <Card
      bordered={false}
      className="flex-col px-5 items-center mb-12 py-8 bg-[#f5fff5] rounded-[32px] border-2 border-black"
    >
      <div className="divide-y-2">
        <div className="rest_header my-3 flex justify-between items-center">
          <h2 className="text-3xl text-black font-bold">{title}</h2>
          <span className="bg-black text-white text-sm md:text-base px-5 py-2 rounded-full font-bold uppercase tracking-wide shadow-sm">
            {subtitle}
          </span>
        </div>
        {normalizeBusinessType(businessType) !== "wholesale" && (
          <div className="order_item">
            <div className="flex items-center gap-4 py-3 my-3">
              <img
                src={restaurantData.bannerImage}
                alt="restaurant_logo"
                className="w-28 h-28 object-cover rounded-full"
              />
              <div>
                <p className="text-2xl text-black font-semibold">
                  {restaurantData?.business?.name +
                    " - " +
                    (restaurantData.pickupLga || "")}
                </p>
                <p className="text-xl text-[#959595]">
                  {totalItems} Items •
                  <NumberFormat
                    value={roundDisplayPrice(totalAmount)}
                    displayType={"text"}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix={" ₦"}
                  />
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="py-5">
          {items.map((item, key) => (
            <CartItem
              key={key}
              data={item}
              businessType={businessType}
              packIndex={key + 1}
              onDelete={() => handleDelete(item.productId)}
            />
          ))}
        </div>

        {(() => {
          const itemBusinessType = items?.[0]?.businessType || businessType;
          const normalizedType = normalizeBusinessType(
            itemBusinessType ||
              restaurantData?.business?.businessType ||
              restaurantData?.businessType ||
              restaurantData?.type
          );

          const isNextDay =
            deliveryType === "next_day" ||
            items?.[0]?.deliveryDateType === "next_day" ||
            items?.[0]?.detail?.deliveryDateType === "next_day" ||
            (typeof title === "string" && title.toLowerCase().includes("next day")) ||
            (typeof subtitle === "string" && subtitle.toLowerCase().includes("tomorrow")) ||
            (typeof subtitle === "string" && subtitle.toLowerCase().includes("set date"));

          const discountMinAmount =
            normalizedType === "retail"
              ? (rateConfig?.checkoutMinDiscountRetail ||
                 rateConfig?.checkoutMinOrderStores ||
                 (minimumOrder && normalizedType === "retail" ? minimumOrder : 10000))
              : normalizedType === "wholesale"
                ? (rateConfig?.checkoutMinDiscountWholesale ||
                   rateConfig?.checkoutMinOrderRawFoods ||
                   (minimumOrder && normalizedType === "wholesale" ? minimumOrder : 120000))
                : isNextDay
                  ? (rateConfig?.checkoutMinDiscountRestaurantND || 10000)
                  : (rateConfig?.checkoutMinDiscountRestaurantSD || rateConfig?.checkoutMinOrderRestaurant || 4000);

          if (!discountMinAmount || discountMinAmount <= 0) return null;

          return (
            <div className="mt-4 text-center bg-gray-50 border border-gray-100 rounded-lg py-4">
              <p className="text-xl font-medium text-gray-700">
                Order above{" "}
                <span className="font-bold">
                  NGN{" "}
                  <NumberFormat
                    value={discountMinAmount}
                    displayType={"text"}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </span>{" "}
                attracts discount
              </p>
            </div>
          );
        })()}

        <div className="my-8">
          <p className="text-lg text-center mb-4">
            By proceeding, you agree to our{" "}
            <span className="text-[#44843F]">Terms of use</span> and{" "}
            <span className="text-[#44843F]">Privacy Policy</span>
          </p>
          <button
            onClick={() => onCheckout(totalAmount)}
            disabled={totalItems === 0}
            className={`flex justify-between w-full rounded-3xl border-2 border-black px-10 py-3 ${
              totalItems === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gray-900 text-white hover:bg-black transition-colors"
            }`}
          >
            <p className="text-xl font-bold">
              {totalItems === 0 ? "Can't Checkout" : `Checkout ${title}`}
            </p>
            <p className="text-xl font-bold">
              <NumberFormat
                value={roundDisplayPrice(totalAmount)}
                displayType={"text"}
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={true}
                prefix={"₦"}
              />
            </p>
          </button>
        </div>
      </div>
    </Card>
  );
};

const RestaurantCartPage = ({ businessType }) => {
  const [restaurantData, setRestaurantData] = useState({});
  const [loading, setLoading] = useState(false);
  const [validatingStock, setValidatingStock] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id: restaurantId } = useParams();

  const query = new URLSearchParams(location.search);
  const initialDeliveryType = query.get("deliveryType");

  const restaurantCarts = useSelector((store) => store.restaurantCart.items);
  const { minimumOrder } = useMinimumOrder(businessType);
  const [weeklyOrderCount, setWeeklyOrderCount] = useState(0);
  const [rateConfig, setRateConfig] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [activeCheckoutType, setActiveCheckoutType] = useState("same_day");

  const bucketOf = (item) => {
    if (businessType !== "restaurant") return "next_day";
    return (item?.detail?.deliveryDateType || item?.deliveryDateType) ===
      "same_day"
      ? "same_day"
      : "next_day";
  };

  const sameDayItems = restaurantCarts.filter(
    (i) => bucketOf(i) === "same_day",
  );
  const nextDayItems = restaurantCarts.filter(
    (i) => bucketOf(i) === "next_day",
  );

  const [activeCartTab, setActiveCartTab] = useState(
    initialDeliveryType === "next_day"
      ? "next_day"
      : sameDayItems.length > 0
        ? "same_day"
        : "next_day",
  );

  useEffect(() => {
    if (initialDeliveryType) {
      setActiveCartTab(initialDeliveryType);
    }
  }, [initialDeliveryType]);

  const openNotificationWithIcon = (type, message, description) => {
    const validTypes = ["success", "error", "warning", "info"];
    const targetType = validTypes.includes(type) ? type : "error";
    const headerTitle = description
      ? message
      : validTypes.includes(type)
        ? targetType.charAt(0).toUpperCase() + targetType.slice(1)
        : type;
    const bodyDesc = description || message;

    if (notification && typeof notification[targetType] === "function") {
      notification[targetType]({
        message: headerTitle,
        description: bodyDesc,
      });
    }
  };

  const fetchRestaurantInfo = async (id) => {
    setLoading(true);
    try {
      const response = await _single_business_data({ id, type: businessType || "restaurant" });
      const restaurant = response.data.data;
      setRestaurantData(restaurant);
      setLoading(false);
    } catch (err) {
      if (err.response) {
        if (err.response.data.message === "Unauthorized") {
          localStorage.removeItem("konnect_token");
          navigate("/");
        }
        openNotificationWithIcon("error", err.response.data.message);
      } else {
        openNotificationWithIcon("Something went wrong", err.message);
      }
      setLoading(false);
    }
  };

  const fetchDashBoardShop = async () => {
    try {
      const res = await _user_dashboard_shop();
      const data = res?.data?.data?.data || res?.data?.data;
      if (data) {
        setWeeklyOrderCount(data.weekly_orders || data.weeklyOrderCount || 0);
        setRateConfig(data.config || data.rateConfig || null);
      }
    } catch (err) {
      console.error("Error fetching shop data:", err);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchRestaurantInfo(restaurantId);
      dispatch(setRestaurant({ restaurantId, businessType }));
    }
    fetchDashBoardShop();
  }, [restaurantId, dispatch, businessType]);

  const handleDelete = (productId) => {
    dispatch(changeQuantity({ productId, quantity: 0 }));
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const handleGoBack = () => {
    const type = (businessType || "").toLowerCase();
    const tabQuery = activeCartTab ? `?tab=${activeCartTab}` : "";
    if (restaurantId && restaurantId !== "undefined") {
      navigate(`/${type || "restaurant"}/${restaurantId}${tabQuery}`);
    } else if (type === "raw_food" || type === "wholesale") {
      navigate(`/raw_food${tabQuery}`);
    } else if (type === "store" || type === "retail") {
      navigate(`/store${tabQuery}`);
    } else {
      navigate(`/${type || "restaurant"}${tabQuery}`);
    }
  };

  const shouldShowDiscountModal = (cartAmount) => {
    const specialMin = rateConfig?.specialMinimumOrder || 0;
    const autoDiscount = rateConfig?.autoDiscountValue || 0;
    const totalSlots = rateConfig?.weekStartSlotLimit || 20;
    return (
      specialMin > 0 &&
      autoDiscount > 0 &&
      cartAmount >= specialMin &&
      weeklyOrderCount < totalSlots
    );
  };

  const proceedToCheckout = (deliveryType, useAutoDiscount = false) => {
    navigate(
      `/${businessType}/${restaurantId}/checkout?deliveryType=${deliveryType}`,
      {
        state: { useAutoDiscount },
      },
    );
  };

  const handleCheckoutCart = async (deliveryType, items, cartTotalAmount) => {
    if (items.length === 0) return;

    try {
      setValidatingStock(true);
      const orderItems = items.map((item) => ({
        menuItemId: item.productId,
        quantity: item.quantity,
      }));

      const response = await _validate_basket_stock({ orderItems });
      const data = response.data?.data;

      if (data.valid) {
        setActiveCheckoutType(deliveryType);
        if (shouldShowDiscountModal(cartTotalAmount)) {
          setShowDiscountModal(true);
        } else {
          proceedToCheckout(deliveryType);
        }
      } else {
        let errorMessage = "Some items are out of stock:\n";
        data.insufficientItems?.forEach((insufficient) => {
          const cartItem = items.find(
            (item) => item.productId === insufficient.menuItemId,
          );
          if (cartItem) {
            errorMessage += `${cartItem.detail?.name || "Unknown Item"}: requested ${insufficient.requested}, available ${insufficient.available}\n`;
          }
          if (cartItem && insufficient.available === 0) {
            dispatch(
              changeQuantity({
                productId: insufficient.menuItemId,
                quantity: 0,
              }),
            );
          } else if (cartItem && insufficient.available < cartItem.quantity) {
            dispatch(
              changeQuantity({
                productId: insufficient.menuItemId,
                quantity: insufficient.available,
              }),
            );
          }
        });
        openNotificationWithIcon("error", errorMessage);
      }
    } catch (err) {
      openNotificationWithIcon(
        "error",
        err?.response?.data?.message || "Validation failed",
      );
    } finally {
      setValidatingStock(false);
    }
  };

  useEffect(() => {
    if (restaurantCarts.length === 0 && !loading) {
      handleGoBack();
    }
  }, [restaurantCarts, loading, navigate, restaurantId]);

  return (
    <>
      <Spin spinning={loading || validatingStock} indicator={antIcon}>
        {restaurantData?.business?.name && restaurantCarts.length > 0 ? (
          <Row justify={"center"} className={"my-2"}>
            <Col xs={22} className={"mb-24"}>
              <BackToRestaurant
                businessId={restaurantId}
                businessType={businessType}
                deliveryType={activeCartTab}
              />

              <div className="mb-6 flex justify-between items-center">
                <h1 className="text-3xl font-gilroyBold text-black">
                  Your Carts
                </h1>
                <Button
                  type="default"
                  className="bg-[#FDF4D7] text-black font-semibold rounded-2xl border border-black hover:bg-yellow-200"
                  onClick={handleGoBack}
                >
                  + Add another{" "}
                  {businessType === "restaurant" ? "menu" : "item"}
                </Button>
              </div>

              {sameDayItems.length > 0 && nextDayItems.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-6 bg-gray-100 p-2 rounded-2xl w-fit border border-gray-200">
                  <button
                    onClick={() => setActiveCartTab("same_day")}
                    className={`px-6 py-3 rounded-xl font-bold text-base md:text-lg transition-all cursor-pointer ${
                      activeCartTab === "same_day"
                        ? "bg-black text-white shadow-md"
                        : "text-gray-700 hover:text-black hover:bg-gray-200/60"
                    }`}
                  >
                    Same Day Cart ({sameDayItems.length})
                  </button>
                  <button
                    onClick={() => setActiveCartTab("next_day")}
                    className={`px-6 py-3 rounded-xl font-bold text-base md:text-lg transition-all cursor-pointer ${
                      activeCartTab === "next_day"
                        ? "bg-black text-white shadow-md"
                        : "text-gray-700 hover:text-black hover:bg-gray-200/60"
                    }`}
                  >
                    {normalizeBusinessType(businessType) === "retail" ? "Retail Cart" : normalizeBusinessType(businessType) === "wholesale" ? "Wholesale Cart" : "Next Day Cart"} ({nextDayItems.length})
                  </button>
                </div>
              )}

              {((activeCartTab === "same_day" && sameDayItems.length > 0) ||
                nextDayItems.length === 0) &&
                sameDayItems.length > 0 && (
                  <SingleCartCard
                    title="Same Day Cart"
                    subtitle="Receive Today"
                    items={sameDayItems}
                    businessType={businessType}
                    handleDelete={handleDelete}
                    handleGoBack={handleGoBack}
                    onCheckout={(totalAmount) =>
                      handleCheckoutCart("same_day", sameDayItems, totalAmount)
                    }
                    rateConfig={rateConfig}
                    restaurantData={restaurantData}
                    minimumOrder={minimumOrder}
                    deliveryType="same_day"
                  />
                )}

              {((activeCartTab === "next_day" && nextDayItems.length > 0) ||
                sameDayItems.length === 0) &&
                nextDayItems.length > 0 && (
                  <SingleCartCard
                    title={normalizeBusinessType(businessType) === "retail" ? "Retail Cart" : normalizeBusinessType(businessType) === "wholesale" ? "Wholesale Cart" : "Next Day Cart"}
                    subtitle={
                      normalizeBusinessType(businessType) === "wholesale"
                        ? "Receive On Set Date"
                        : "Receive Tomorrow"
                    }
                    items={nextDayItems}
                    businessType={businessType}
                    handleDelete={handleDelete}
                    handleGoBack={handleGoBack}
                    onCheckout={(totalAmount) =>
                      handleCheckoutCart("next_day", nextDayItems, totalAmount)
                    }
                    rateConfig={rateConfig}
                    restaurantData={restaurantData}
                    minimumOrder={minimumOrder}
                    deliveryType="next_day"
                  />
                )}

              <LeaveMessageDrawer />
            </Col>
          </Row>
        ) : (
          <div className="flex items-center justify-center h-screen">
            <p>Loading Order Summary...</p>
          </div>
        )}
      </Spin>
      <CartDiscountChoiceModal
        open={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        autoDiscountValue={rateConfig?.autoDiscountValue}
        rateConfig={rateConfig}
        weeklyOrderCount={weeklyOrderCount}
        onUseAutoDiscount={() => {
          setShowDiscountModal(false);
          proceedToCheckout(activeCheckoutType, true);
        }}
        onSaveForThirdOrder={() => {
          setShowDiscountModal(false);
          proceedToCheckout(activeCheckoutType, false);
        }}
      />
      <FooterDisplay />
    </>
  );
};

export default RestaurantCartPage;
